import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Zap, ArrowRight, Play, Chrome } from 'lucide-react'
import { ExtensionDemoButton } from '../ExtensionDemo'

export default function Hero() {
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        setIsVisible(true)
    }, [])

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 animated-gradient-bg" />

            {/* Floating Blobs */}
            <div className="blob w-96 h-96 bg-primary-500/30 top-20 -left-48" />
            <div className="blob w-80 h-80 bg-accent-500/30 bottom-20 -right-40" style={{ animationDelay: '-5s' }} />
            <div className="blob w-64 h-64 bg-ocean-500/30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ animationDelay: '-10s' }} />

            {/* Grid Pattern Overlay */}
            <div className="absolute inset-0 bg-grid-pattern opacity-30" />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/50 to-slate-950" />

            {/* Content */}
            <div className="relative z-10 section-container py-20 text-center">
                {/* Badge */}
                <div
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8
                      transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                    <Sparkles className="w-4 h-4 text-primary-400" />
                    <span className="text-sm font-medium text-white/80">AI Agent for Technical Writers</span>
                </div>

                {/* Main Heading */}
                <h1
                    className={`text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-tight text-balance
                      transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                    Don't Just Write.
                    <br />
                    <span className="gradient-text">Deploy an Agent.</span>
                </h1>

                {/* Subheading */}
                <p
                    className={`text-lg sm:text-xl lg:text-2xl text-white/60 max-w-2xl mx-auto mb-10 text-balance
                      transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                    Research, structure, and draft technical content in one workflow.
                    InkSpot isn't just an assistant; it's your full-cycle writing partner.
                </p>

                {/* CTA Buttons */}
                <div
                    className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-12
                      transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                    <Link to="/agent" className="btn-primary text-lg px-8 py-4 group">
                        <Zap className="w-5 h-5 mr-2" />
                        Start Agent
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>

                    <ExtensionDemoButton variant="secondary" className="text-lg px-8 py-4" />
                </div>

                {/* Social Proof */}
                <div
                    className={`flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12
                      transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                >
                    <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                            {[
                                'https://randomuser.me/api/portraits/women/44.jpg',
                                'https://randomuser.me/api/portraits/men/32.jpg',
                                'https://randomuser.me/api/portraits/women/68.jpg',
                                'https://randomuser.me/api/portraits/men/75.jpg',
                                'https://randomuser.me/api/portraits/women/90.jpg',
                            ].map((src, i) => (
                                <img
                                    key={i}
                                    src={src}
                                    alt={`Writer ${i + 1}`}
                                    className="w-8 h-8 rounded-full border-2 border-slate-950 object-cover"
                                />
                            ))}
                        </div>
                        <span className="text-white/60 text-sm">1,000+ writers</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            ))}
                        </div>
                        <span className="text-white/60 text-sm">4.9/5 rating</span>
                    </div>

                    {/* Product Hunt Badge */}
                    <a
                        href="https://www.producthunt.com/products/inkspot-2?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-inkspot-2"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="transition-transform hover:scale-105"
                    >
                        <img
                            src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1060585&theme=dark&t=1768011148654"
                            alt="InkSpot - AI writing assistant that eliminates clichés in real time | Product Hunt"
                            width="250"
                            height="54"
                            className="h-[40px] w-auto"
                        />
                    </a>
                </div>

                {/* Demo Preview */}
                <div
                    className={`mt-16 max-w-4xl mx-auto
                      transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                    <div className="relative rounded-2xl overflow-hidden glass-card p-1 animate-glow">
                        <div className="relative rounded-xl overflow-hidden bg-slate-900">
                            {/* Mock Editor */}
                            <div className="p-6 sm:p-8">
                                {/* Toolbar */}
                                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-500" />
                                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                        <div className="w-3 h-3 rounded-full bg-green-500" />
                                    </div>
                                    <span className="text-white/40 text-sm ml-4">draft-article.txt</span>
                                </div>

                                {/* Content */}
                                <div className="font-mono text-sm sm:text-base text-white/80 leading-relaxed">
                                    <p className="mb-4">
                                        The startup ecosystem is{' '}
                                        <span className="cliche-highlight px-1 rounded">a double-edged sword</span>
                                        . On one hand, innovation{' '}
                                        <span className="cliche-highlight-medium px-1 rounded">at the end of the day</span>
                                        {' '}drives progress. But founders need to{' '}
                                        <span className="cliche-highlight px-1 rounded">think outside the box</span>
                                        {' '}to succeed.
                                    </p>
                                    <div className="mt-4 p-4 rounded-lg bg-primary-500/10 border border-primary-500/30">
                                        <div className="flex items-start gap-3">
                                            <Sparkles className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-primary-300 font-medium mb-1">3 clichés detected</p>
                                                <p className="text-white/60 text-sm">Click on highlighted phrases to see AI-powered suggestions</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
                    <div className="w-1 h-2 rounded-full bg-white/40" />
                </div>
            </div>
        </section>
    )
}
